<template>
  <section class="studio-comparison" aria-label="Kontaktstatistik nach Studio">
    <div class="studio-comparison__sources" role="group" aria-label="Kontaktart">
      <button v-for="source in sources" :key="source.key" type="button" class="studio-comparison__source"
        :class="{ 'is-active': value.source === source.key }" :aria-pressed="value.source === source.key" :disabled="disabled"
        @click="update({ source: source.key })">
        {{ source.title }} <span>{{ formatNumber(comparison.sourceTotals[source.key]) }}</span>
      </button>
    </div>

    <div class="studio-comparison__tools">
      <v-text-field :value="value.search" @input="update({ search: $event || '' })" label="Studio suchen" prepend-inner-icon="mdi-magnify"
        clearable outlined dense hide-details :disabled="disabled" class="studio-comparison__search" />
      <v-checkbox :input-value="value.onlyRed" @change="update({ onlyRed: Boolean($event) })" label="Nur rote Werte"
        color="#fca5a5" dense hide-details :disabled="disabled" class="studio-comparison__filter" />
      <span class="studio-comparison__shown" role="status" aria-live="polite">{{ visibleRows.length }} / {{ comparison.rows.length }} Studios</span>
      <v-btn small text :disabled="disabled || value.sortKey === 'outliers'" @click="update({ sortKey: 'outliers', sortDirection: 'asc' })">
        Rote Werte zuerst
      </v-btn>
    </div>

    <div class="studio-comparison__legend">
      <v-tooltip v-for="entry in legend" :key="entry.tone" bottom>
        <template v-slot:activator="{ on, attrs }">
          <span tabindex="0" class="studio-comparison__legend-item" v-bind="attrs" v-on="on" :aria-label="entry.label + ': ' + entry.description">
            <i class="studio-comparison__dot" :class="'studio-comparison__dot--' + entry.tone" aria-hidden="true"></i>
            <span aria-hidden="true">{{ entry.symbol }}</span>{{ entry.label }}
          </span>
        </template>
        <span>{{ entry.description }}</span>
      </v-tooltip>
    </div>
    <p class="studio-comparison__context">
      {{ formatNumber(comparison.reference.contacts) }} Kontakte · {{ formatNumber(comparison.reference.people) }} zugeordnete Personen
      <span v-if="comparison.reference.unlinkedContacts"> · {{ formatNumber(comparison.reference.unlinkedContacts) }} Kontakte ohne vollständige Zuordnung</span>
      <span v-if="comparison.reference.hiddenContacts"> · {{ formatNumber(comparison.reference.hiddenContacts) }} Kontakte ausgeblendet</span>
    </p>

    <div v-if="comparison.rows.length" class="studio-comparison__scroll">
      <table class="studio-comparison__table">
        <caption class="studio-comparison__sr-only">{{ comparison.source.title }} nach Studio</caption>
        <thead>
          <tr>
            <th scope="col" :aria-sort="ariaSort('name')" class="studio-comparison__name">
              <button type="button" @click="sort('name')" :disabled="disabled">Studio <span aria-hidden="true">{{ sortSymbol('name') }}</span></button>
              <small v-if="value.sortKey === 'outliers'">Rote Werte zuerst</small>
            </th>
            <th scope="col" :aria-sort="ariaSort('total')">
              <button type="button" @click="sort('total')" :disabled="disabled">Kontakte <span aria-hidden="true">{{ sortSymbol('total') }}</span></button>
            </th>
            <th v-for="metric in metrics" :key="metric.key" scope="col" :aria-sort="ariaSort(metric.key)">
              <button type="button" @click="sort(metric.key)" :disabled="disabled" :aria-label="metric.detailLabel + ': nach Quote sortieren'">
                {{ metric.label }} <span aria-hidden="true">{{ sortSymbol(metric.key) }}</span>
              </button>
              <v-tooltip bottom :disabled="!metric.beforeContactKey">
                <template v-slot:activator="{ on, attrs }">
                  <small v-bind="attrs" v-on="on" :tabindex="metric.beforeContactKey ? 0 : null">
                    {{ formatNumber(comparison.reference.metrics[metric.key].count) }} gesamt · Ø {{ formatPercent(comparison.reference.metrics[metric.key].rate) }}
                  </small>
                </template>
                <span>{{ existingOutcomeHint(comparison.reference.metrics[metric.key].beforeContact) }}</span>
              </v-tooltip>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="studio in visibleRows" :key="studio.studioId" :class="{ 'has-red': studio.redCount > 0 }">
            <th scope="row" class="studio-comparison__name">
              <button type="button" class="studio-comparison__name-link" :disabled="!canOpen(studio)" @click="open(studio, 'total')">
                {{ studio.name }}
              </button>
              <small v-if="studioHint(studio)">{{ studioHint(studio) }}</small>
            </th>
            <td>
              <button type="button" class="studio-comparison__contacts" :disabled="!canOpen(studio)" @click="open(studio, 'total')"
                :aria-label="studio.name + ': ' + formatNumber(studio.row.total) + ' Kontakte ansehen'">
                <strong>{{ formatNumber(studio.row.total) }}</strong>
                <small>{{ formatNumber(studio.row.linkedPeople) }} Personen</small>
              </button>
            </td>
            <td v-for="metric in metrics" :key="metric.key">
              <v-tooltip bottom :disabled="!metric.beforeContactKey">
                <template v-slot:activator="{ on, attrs }">
                  <span class="studio-comparison__metric-wrap" v-bind="attrs" v-on="on"
                    :tabindex="!canOpen(studio) && metric.beforeContactKey ? 0 : null" :aria-label="metricDescription(studio, metric)">
                    <button type="button" class="studio-comparison__metric" :class="'studio-comparison__metric--' + studio.metrics[metric.key].tone"
                      :disabled="!canOpen(studio)" @click="open(studio, metric.key)"
                      :aria-label="metricDescription(studio, metric)" v-on="on">
                      <span class="studio-comparison__rate"><span aria-hidden="true">{{ studio.metrics[metric.key].symbol }}</span><strong>{{ formatPercent(studio.metrics[metric.key].rate) }}</strong></span>
                      <small>{{ formatNumber(studio.metrics[metric.key].count) }} Personen</small>
                    </button>
                  </span>
                </template>
                <span>{{ existingOutcomeHint(studio.metrics[metric.key].beforeContact) }}</span>
              </v-tooltip>
            </td>
          </tr>
          <tr v-if="!visibleRows.length"><td colspan="5" class="studio-comparison__empty">
            Keine Studios für diese Auswahl.
            <v-btn text small @click="update({ search: '', onlyRed: false })" :disabled="disabled">Filter zurücksetzen</v-btn>
          </td></tr>
        </tbody>
      </table>
    </div>
    <p v-else class="studio-comparison__empty">Keine Studios vorhanden.</p>
  </section>
</template>

<script>
import { COMPARISON_LEGEND, CONTACT_SOURCES, OUTCOME_METRICS, buildStudioComparison, selectStudioRows, existingOutcomeHint } from "./acquisitionStudioComparison"

export default {
  name: "AcquisitionStudioComparison",
  props: {
    studios: { type: Array, default: () => [] },
    value: { type: Object, required: true },
    detailsAllowed: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  data: () => ({ sources: CONTACT_SOURCES, metrics: OUTCOME_METRICS, legend: COMPARISON_LEGEND }),
  computed: {
    comparison() { return buildStudioComparison(this.studios, this.value.source) },
    visibleRows() { return selectStudioRows(this.comparison, this.value) },
  },
  methods: {
    existingOutcomeHint,
    update(patch) { if (!this.disabled) this.$emit("input", { ...this.value, ...patch }) },
    formatNumber(value) { return Number(value || 0).toLocaleString("de-CH") },
    formatPercent(value) { return value === null ? "–" : value.toLocaleString("de-CH", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + " %" },
    canOpen(studio) { return !this.disabled && this.detailsAllowed && studio.row.total > 0 },
    open(studio, metric) {
      if (!this.canOpen(studio)) return
      // A zero outcome still lets the user inspect this studio's contacts.
      const selection = metric !== "total" && studio.row[metric] > 0 ? metric : "total"
      this.$emit("open-details", { row: studio.row, studio, metric: selection })
    },
    sort(key) {
      const direction = this.value.sortKey === key ? (this.value.sortDirection === "asc" ? "desc" : "asc") : key === "total" ? "desc" : "asc"
      this.update({ sortKey: key, sortDirection: direction })
    },
    ariaSort(key) { return this.value.sortKey !== key ? "none" : this.value.sortDirection === "desc" ? "descending" : "ascending" },
    sortSymbol(key) { return this.value.sortKey !== key ? "↕" : this.value.sortDirection === "desc" ? "↓" : "↑" },
    studioHint(studio) {
      const hints = []
      if (studio.row.unlinkedContacts) hints.push(this.formatNumber(studio.row.unlinkedContacts) + " Kontakte unvollständig zugeordnet")
      if (studio.row.hiddenContacts) hints.push(this.formatNumber(studio.row.hiddenContacts) + " Kontakte ausgeblendet")
      return hints.join(" · ")
    },
    metricDescription(studio, metric) {
      const item = studio.metrics[metric.key]
      const delta = item.delta === null ? "" : "; " + (item.delta > 0 ? "+" : "") + item.delta.toLocaleString("de-CH") + " Prozentpunkte zu Ø"
      const action = !this.canOpen(studio) ? "" : item.count ? "; Personen ansehen" : "; alle Kontakte ansehen"
      return `${studio.name}: ${metric.detailLabel}, ${item.count} von ${studio.row.linkedPeople} Personen, ${this.formatPercent(item.rate)}. ${item.reason}${delta}${action}. ${existingOutcomeHint(item.beforeContact)}`
    },
  },
}
</script>

<style scoped>
.studio-comparison { color: #e2e8f0; }
.studio-comparison__sources { display: flex; flex-wrap: wrap; gap: 6px; padding: 0 16px 12px; }
.studio-comparison__source { display: flex; align-items: center; gap: 10px; padding: 7px 12px; min-height: 38px; border: 1px solid #526071; border-radius: 7px; color: #d7e0ec; font-size: 0.86rem; }
.studio-comparison__source span { font-size: 0.78rem; font-variant-numeric: tabular-nums; opacity: 0.85; }
.studio-comparison__source.is-active { color: #d8eeff; background: #293e52; border-color: #83bce8; }
.studio-comparison__source:not(:disabled):hover { background: #354455; }
.studio-comparison__tools { display: flex; flex-wrap: wrap; align-items: center; gap: 10px 16px; padding: 0 16px 10px; }
.studio-comparison__search { flex: 1 1 210px; max-width: 300px; }
.studio-comparison__filter { flex: 0 0 auto; padding: 0; margin: 0; }
.studio-comparison__filter ::v-deep .v-label { font-size: 0.8rem; }
.studio-comparison__shown { margin-left: auto; color: #b8c4d4; font-size: 0.78rem; white-space: nowrap; }
.studio-comparison__legend { display: flex; flex-wrap: wrap; align-items: baseline; gap: 7px 16px; padding: 0 16px 9px; font-size: 0.75rem; color: #cbd5e1; }
.studio-comparison__legend-item { display: inline-flex; align-items: center; gap: 5px; min-height: 28px; white-space: nowrap; cursor: help; }
.studio-comparison__dot { width: 7px; height: 7px; border-radius: 50%; background: #94a3b8; }
.studio-comparison__dot--below { background: #fca5a5; }
.studio-comparison__dot--near { background: #fdba74; }
.studio-comparison__dot--above { background: #6ee7b7; }
.studio-comparison__context { padding: 0 16px; margin: 0 0 10px; font-size: 0.75rem; color: #b8c4d4; line-height: 1.55; }
.studio-comparison__scroll { max-height: 70vh; overflow: auto; border-top: 1px solid #43505d; border-bottom: 1px solid #43505d; }
.studio-comparison__table { width: 100%; min-width: 660px; border-spacing: 0; table-layout: fixed; }
.studio-comparison__table th, .studio-comparison__table td { padding: 5px 8px; text-align: center; border-bottom: 1px solid #3b424a; vertical-align: middle; font-variant-numeric: tabular-nums; }
.studio-comparison__table th { font-weight: 500; }
.studio-comparison__table .studio-comparison__name { width: 29%; text-align: left; padding-left: 16px; position: sticky; left: 0; z-index: 1; background: #2a2a2a; }
.studio-comparison__table thead th { position: sticky; top: 0; z-index: 2; background: #242a31; padding-top: 9px; padding-bottom: 9px; }
.studio-comparison__table thead .studio-comparison__name { z-index: 3; background: #242a31; }
.studio-comparison__table thead button { min-height: 25px; font-size: 0.8rem; color: #e2e8f0; }
.studio-comparison__table thead small { margin-top: 2px; color: #c0cbd8; }
.studio-comparison__table small { display: block; font-size: 0.72rem; font-weight: 400; line-height: 1.35; }
.studio-comparison__table tbody tr:hover td, .studio-comparison__table tbody tr:hover .studio-comparison__name { background-color: #30353a; }
.studio-comparison__table tbody tr.has-red .studio-comparison__name { box-shadow: inset 3px 0 #fca5a5; }
.studio-comparison__name-link { text-align: left; color: #e2e8f0; font-size: 0.84rem; font-weight: 600; min-height: 26px; overflow-wrap: anywhere; }
.studio-comparison__name-link + small { margin-top: 2px; color: #bac5d3; }
.studio-comparison__name-link:not(:disabled):hover { text-decoration: underline; text-underline-offset: 3px; }
.studio-comparison__contacts { padding: 3px 5px; min-height: 40px; color: #e2e8f0; }
.studio-comparison__contacts strong { font-size: 0.93rem; }
.studio-comparison__contacts small { color: #bbc8d7; }
.studio-comparison__metric-wrap { display: block; }
.studio-comparison__metric-wrap:focus-visible, .studio-comparison__table th small:focus-visible { outline: 2px solid #7dd3fc; outline-offset: 2px; }
.studio-comparison__metric { width: 100%; min-height: 42px; padding: 4px 5px; border-radius: 5px; color: #cbd5e1; background: #363c44; }
.studio-comparison__rate { display: flex; align-items: center; justify-content: center; gap: 5px; white-space: nowrap; font-size: 0.87rem; }
.studio-comparison__metric small { margin-top: 1px; color: inherit; }
.studio-comparison__metric--below { background: #482b31; color: #fecaca; }
.studio-comparison__metric--near { background: #473724; color: #fed7aa; }
.studio-comparison__metric--above { background: #244037; color: #a7f3d0; }
.studio-comparison__metric:not(:disabled):hover { filter: brightness(1.17); }
.studio-comparison__contacts:not(:disabled):hover { background: #34414e; border-radius: 5px; }
.studio-comparison button:not(:disabled) { cursor: pointer; }
.studio-comparison button:focus-visible, .studio-comparison__legend-item:focus-visible { outline: 2px solid #7dd3fc; outline-offset: 2px; }
.studio-comparison__empty { padding: 22px 16px !important; text-align: center; color: #cbd5e1; }
.studio-comparison__sr-only { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
@media (max-width: 699px) {
  .studio-comparison__sources { gap: 5px; padding-left: 12px; padding-right: 12px; }
  .studio-comparison__source { padding: 6px 9px; gap: 6px; font-size: 0.8rem; }
  .studio-comparison__tools { padding-left: 12px; padding-right: 12px; gap: 9px; }
  .studio-comparison__search { flex-basis: 100%; max-width: none; }
  .studio-comparison__shown { margin-left: auto; }
  .studio-comparison__table { min-width: 640px; }
  .studio-comparison__table .studio-comparison__name { width: 154px; padding-left: 12px; }
}
@media (pointer: coarse) {
  .studio-comparison__source, .studio-comparison__table button { min-height: 44px; }
  .studio-comparison__table th, .studio-comparison__table td { padding-top: 6px; padding-bottom: 6px; }
}
</style>
